import { capitalize } from 'lodash'

/**
 * Acronyms that should be fully uppercased
 */
const ACRONYMS = new Set([
  `ai`,
  `smc`,
  `sms`,
  `api`,
  `url`,
  `id`,
  `html`,
  `css`,
  `json`,
  `xml`,
  `http`,
  `https`,
  `pms`,
  `sql`,
  `aws`,
  `utm`,
  `cpc`,
  `cpm`,
  `roi`,
  `kpi`,
  `seo`,
  `ppc`,
  `crm`,
  `lsa`,
])

/**
 * Words that should remain lowercase in title case (unless they're the first or last word)
 */
const LOWERCASE_WORDS = new Set([
  // Articles
  `a`,
  `an`,
  `the`,
  // Coordinating conjunctions
  `and`,
  `but`,
  `or`,
  `nor`,
  `for`,
  `yet`,
  `so`,
  // Short prepositions (3 letters or less)
  `as`,
  `at`,
  `by`,
  `in`,
  `of`,
  `on`,
  `to`,
  `up`,
  `via`,
  // Others
  `is`,
])

/**
 * Matches a single word: letters/digits plus any internal apostrophes. Everything
 * it does not match (spaces, parentheses, hyphens, slashes) is punctuation and is
 * left exactly where the caller put it.
 */
const wordPattern = () => /[a-z0-9]+(?:'[a-z]+)*/gi

/**
 * Converts a string to title case following proper English title capitalization rules:
 * - Common acronyms (SMC, API, URL, etc.) are fully uppercased
 * - First and last words are always capitalized
 * - Articles, conjunctions, and short prepositions are lowercase (unless first/last)
 * - All other words are capitalized
 * - Punctuation is preserved, so labels like `Hygiene Due (30 Days)` survive intact
 *
 * @param str - The string to convert to title case
 * @returns The string in title case
 *
 * @example
 * titleCase('hello world') // => 'Hello World'
 * titleCase('the quick brown fox') // => 'The Quick Brown Fox'
 * titleCase('smc dental group') // => 'SMC Dental Group'
 * titleCase('api url id') // => 'API URL ID'
 * titleCase('hygiene due (30 days)') // => 'Hygiene Due (30 Days)'
 */
export function titleCase(str: string | undefined): string {
  if (!str) return ``

  const trimmed = str.trim()
  const wordCount = trimmed.match(wordPattern())?.length ?? 0
  if (wordCount === 0) return trimmed

  let index = -1

  return trimmed.replace(wordPattern(), (match) => {
    index += 1
    const word = match.toLowerCase()

    // Uppercase acronyms
    if (ACRONYMS.has(word)) {
      return word.toUpperCase()
    }

    // Always capitalize first and last words
    if (index === 0 || index === wordCount - 1) {
      return capitalize(word)
    }

    // Keep small words lowercase
    if (LOWERCASE_WORDS.has(word)) {
      return word
    }

    // Capitalize all other words
    return capitalize(word)
  })
}
